import { useState } from "react";
import PropTypes from "prop-types";
import {
	NavLink as RouterLink,
	matchPath,
	useLocation,
} from "react-router-dom";
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
	Box,
	List,
	Collapse,
	ListItemText,
	ListItemIcon,
	ListItemButton,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";

const ListItemStyle = styled((props) => (
	<ListItemButton disableGutters {...props} />
))(({ theme }) => ({
	fontSize:12,
	height: 48,
	position: "relative",
	textTransform: "capitalize",
	paddingLeft: theme.spacing(3),
	paddingRight: theme.spacing(1.5),
	color: theme.palette.text.secondary,
	"&:before": {
		top: 0,
		right: 0,
		width: 3,
		bottom: 0,
		content: "''",
		display: "none",
		position: "absolute",
		borderTopLeftRadius: 2,
		borderBottomLeftRadius: 2,
		backgroundColor: theme.palette.primary.main,
	},
}));

const ListItemIconStyle = styled(ListItemIcon)({
	width: 15,
	height: 15,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
});

NavItem.propTypes = {
	item: PropTypes.object,
	active: PropTypes.func,
};

function NavItem({ item, active, onCloseSideBar }) {
	const theme = useTheme();
	const isActiveRoot = active(item.path);
	const { title, path, icon, info, children } = item;
	const [open, setOpen] = useState(isActiveRoot);

	const handleOpen = () => {
		setOpen((prev) => !prev);
	};

	const activeRootStyle = {
		color: "primary.main",
		fontWeight: "fontWeightMedium",
		bgcolor: alpha(
			theme.palette.primary.main,
			theme.palette.action.selectedOpacity
		),
		"&:before": { display: "block" },
	};

	const activeSubStyle = {
		color: "text.primary",
		fontWeight: "fontWeightMedium",
	};

	if (children) {
		return (
			<>
				<ListItemStyle
					onClick={() => {
						handleOpen();
					}}
					sx={{
						...(isActiveRoot && activeRootStyle),
					}}
				>
					<ListItemIconStyle>{icon && icon}</ListItemIconStyle>
					<ListItemText disableTypography primary={title} />
					{info && info}
					{open ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
				</ListItemStyle>

				<Collapse in={open} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{children.map((item) => {
							const { title, path } = item;
							const isActiveSub = active(path);

							return (
								<ListItemStyle
									key={title}
									component={RouterLink}
									to={path}
									sx={{
										...(isActiveSub && activeSubStyle),
									}}
								>
									<ListItemIconStyle>
										<Box
											component="span"
											sx={{
												width: 2,
												height: 2,
												display: "flex",
												borderRadius: "50%",
												alignItems: "center",
												justifyContent: "center",
												bgcolor: "text.disabled",
												transition: (theme) =>
													theme.transitions.create("transform"),
												...(isActiveSub && {
													transform: "scale(2)",
													bgcolor: "primary.main",
												}),
											}}
										/>
									</ListItemIconStyle>
									<ListItemText disableTypography primary={title} />
								</ListItemStyle>
							);
						})}
					</List>
				</Collapse>
			</>
		);
	}

	return (
		<ListItemStyle
			component={RouterLink}
			to={path}
			sx={{
				...(isActiveRoot && activeRootStyle),
			}}
		>
			<ListItemIconStyle>{icon && icon}</ListItemIconStyle>
			<ListItemText disableTypography primary={title} />
			{info && info}
		</ListItemStyle>
	);
}

NavSection.propTypes = {
	navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
	const { pathname } = useLocation();
	const userRole = localStorage.getItem("_impr");
	const match = (path) =>
		path ? !!matchPath({ path, end: false }, pathname) : false;

	return (
		<Box {...other} sx={{overflow: 'hidden'}}>
			<List
				disablePadding
				sx={{
					overflow: "hidden",
				}}
			>
				{navConfig.map((item) => {
					if (item.accessorsRole.includes(userRole)) {
						return <NavItem key={item.title} item={item} active={match} />;
					}
					return null;
				})}
			</List>
		</Box>
	);
}
